import { ReactElement, useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { usePageTracking } from '../../../hooks';
import { AnalyticsPageCategory, AnalyticsPageType } from '../../../models';

import {
	Comparison,
	Project,
	AppState,
	AppThunkDispatch,
	ComparisonActions,
	comparisonRemoveMap,
	comparisonSetProjects,
	comparisonFetch,
	comparisonClear
} from '../../../redux';
import { PageContainer } from '../../molecules';
import { ComparisonViewer } from '../../organisms';

interface IComparisonPageProps {
	comparison?: Comparison;
	removeProject: (index: number) => void;
	reorderProjects: (projects: Project[]) => void;
	fetchComparison: (comparisonId: string) => void;
	clearComparison: () => void;
}

interface IRoutingParams {
	comparisonId?: string;
}

function ComparisonPageView(props: IComparisonPageProps): ReactElement {
	const {
		comparison,
		removeProject,
		reorderProjects,
		fetchComparison,
		clearComparison
	} = props;
	const { comparisonId } = useParams<IRoutingParams>();

	const history = useHistory();
	usePageTracking(
		AnalyticsPageCategory.Content,
		AnalyticsPageType.Comparison,
		comparisonId
	);

	useEffect(() => {
		return clearComparison;
	}, []);

	useEffect(() => {
		if (!comparison && !comparisonId) {
			history.push('/content');
		}
		if (comparison && comparison.id && !comparisonId) {
			history.push(`comparison/${comparison.id}`);
		}
		if (shouldFetchComparison() && comparisonId) {
			fetchComparison(comparisonId);
		}
	}, [comparison, comparisonId]);

	function shouldFetchComparison(): boolean {
		return (
			(comparisonId !== undefined && !comparison) ||
			(comparisonId !== undefined &&
				comparison !== undefined &&
				comparison.id !== comparisonId)
		);
	}

	return (
		<PageContainer id="comparison-page" noPadding>
			{comparison && (
				<ComparisonViewer
					comparison={comparison}
					onRemoveProject={removeProject}
					reorderProjects={reorderProjects}
				/>
			)}
		</PageContainer>
	);
}

function mapStateToProps(state: AppState) {
	return {
		comparison: state.comparisons.comparison
	};
}

function mapDispatchToProps(dispatch: AppThunkDispatch<ComparisonActions>) {
	return {
		removeProject: (index: number) => dispatch(comparisonRemoveMap(index)),
		reorderProjects: (projects: Project[]) =>
			dispatch(comparisonSetProjects(projects)),
		fetchComparison: (comparisonId: string) =>
			dispatch(comparisonFetch(comparisonId)),
		clearComparison: () => dispatch(comparisonClear())
	};
}

export const ComparisonPage = connect(
	mapStateToProps,
	mapDispatchToProps
)(ComparisonPageView);
